import {
  ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit,
  signal, Type,
} from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { SeoService } from '../../../core/services/seo.service';
import { DecisionTreeService } from '../../../core/services/decision-tree.service';
import { AnfrageWizardStore } from '../store/anfrage-wizard.store';
import { DecisionTrees, Trade } from '../../../core/models/decision-tree.model';
import { TradePickerComponent } from '../trade-picker/trade-picker.component';
import { SingleSelectRendererComponent } from '../renderers/single-select-renderer.component';
import { InfoStepRendererComponent } from '../renderers/info-step-renderer.component';
import { EndStepRendererComponent } from '../end-step/end-step-renderer.component';
import { NodeRendererInputs, NodeRendererRegistry } from '../renderers/node-renderer';

/**
 * Generic wizard route. Shell + progress + back. The current node's renderer
 * is chosen via {@link NodeRendererRegistry}, so adding a new node type is a
 * matter of registering one component — the shell itself stays untouched.
 */
@Component({
  selector: 'app-anfrage-wizard',
  standalone: true,
  imports: [NgComponentOutlet, RouterLink, TradePickerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './wizard.component.html',
  styleUrl: './wizard.component.scss',
})
export class WizardComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly trees = inject(DecisionTreeService);
  private readonly seo = inject(SeoService);
  /** Public for template binding. */
  readonly store = inject(AnfrageWizardStore);

  private readonly destroy$ = new Subject<void>();
  private readonly registry = new NodeRendererRegistry();

  readonly spec = signal<DecisionTrees | null>(null);
  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly tradePicker = signal<Trade[] | null>(null);

  readonly progress = this.store.progress;
  readonly currentNode = this.store.currentNode;

  readonly rendererComponent = computed<Type<NodeRendererInputs> | null>(() => {
    const n = this.currentNode();
    return n ? this.registry.resolve(n.type) : null;
  });

  readonly rendererInputs = computed<Record<string, unknown>>(() => {
    const n = this.currentNode();
    const k = this.store.currentNodeKey();
    if (!n || !k) return {};
    return { node: n, nodeKey: k };
  });

  ngOnInit(): void {
    this.seo.apply({
      title: 'Anfrage starten – Handwerker schnell finden | Craftly',
      description: 'Beantworten Sie ein paar Fragen und wir matchen Sie mit passenden Handwerkern. Kostenlos und unverbindlich.',
      path: '/anfrage/neu',
    });

    this.registry.register('single_select', SingleSelectRendererComponent);
    this.registry.register('info_step', InfoStepRendererComponent);
    this.registry.register('summary_and_extras', EndStepRendererComponent);

    this.trees.load().pipe(takeUntil(this.destroy$)).subscribe({
      next: (spec) => {
        this.spec.set(spec);
        this.loading.set(false);
        this.bootstrapFromQueryParam(spec);
      },
      error: () => {
        this.loadError.set('Konnte die Anfrage-Konfiguration nicht laden.');
        this.loading.set(false);
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private bootstrapFromQueryParam(spec: DecisionTrees): void {
    const trade = this.route.snapshot.queryParamMap.get('trade');
    if (trade && this.store.enter(spec, trade)) {
      return;
    }
    // No trade hinted — show the picker.
    this.tradePicker.set(Object.values(spec.trades));
  }

  pickTrade(trade: Trade): void {
    const spec = this.spec();
    if (!spec) return;
    this.store.enter(spec, trade.id);
    this.tradePicker.set(null);
    void this.router.navigate(['.'], {
      relativeTo: this.route,
      queryParams: { trade: trade.id },
      replaceUrl: true,
    });
  }

  back(): void { this.store.back(); }

  cancel(): void {
    this.store.reset();
    void this.router.navigate(['/']);
  }
}
